import { Autocomplete, AutocompleteProps, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import React from "react";
import { Control } from "react-hook-form/dist/types/form";
import { useTranslation } from "react-i18next";

export function MuiRhfAutocompleteListField<LIST_TYPE>(props: {
  name: string;
  label: string;
  list: LIST_TYPE[];
  getListLabel?: (entry: LIST_TYPE) => string;
  getListKey?: (entry: LIST_TYPE) => string | number | boolean;
  noSelectionText?: string;
  control?: Control<any, any>;
  size?: "small";
  required?: boolean;
} & Partial<
  Omit<AutocompleteProps<LIST_TYPE, false, false, false>, "onChange" | "onBlur" | "value" | "name" | "error">
>) {
  const {
    name,
    label,
    list,
    getListLabel,
    getListKey,
    noSelectionText,
    control,
    required,
    ...autoCompleteAndFormControlProps
  } = props;
  const { t } = useTranslation();

  function getListKeyOrDefault(value: any) {
    if (!list || list.length === 0) {
      return "";
    }
    let result: string | number | boolean = "";
    if (value !== null && value !== undefined) {
      if (getListKey) {
        result = getListKey(value);
      } else {
        result = value.toString();
      }
    }
    if (result === null || result === undefined) {
      result = "";
    }
    return result;
  }

  function getListLabelOrDefault(value: LIST_TYPE) {
    if (value === undefined || value === null || list.length === 0) {
      return "";
    }
    if (!getListLabel) {
      return (value as any).toString();
    }
    return getListLabel(value) || value;
  }

  return (
    <>
      <Controller
        name={name}
        control={control}
        rules={{
          required: {
            value: required || false,
            message: t("FormErrors.mustBeDefined"),
          },
        }}
        render={({ field, fieldState: { error } }) => {
          return (
            <Autocomplete
              id={"select-" + name}
              options={list}
              getOptionLabel={getListLabelOrDefault}
              isOptionEqualToValue={(option, value) => getListKeyOrDefault(option) === getListKeyOrDefault(value)}
              // null instead of undefined keeps the Autocomplete controlled, so a later reset() (e.g. loading an
              // existing user) shows the new value
              value={(field.value ?? null) as LIST_TYPE}
              onChange={(e, value) => field.onChange(value)}
              sx={{ marginTop: 0, "& .MuiFormControl-root": { marginTop: 0 } }} // Remove the margin from the form control
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={label}
                  error={!!error}
                  helperText={error?.message}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                />
              )}
              noOptionsText={noSelectionText || "-"}
              {...autoCompleteAndFormControlProps}
            />
          );
        }}
      />
    </>
  );
}
